import { EyeIcon } from '@heroicons/react/outline';
import { getConversationMetadata } from 'api/v2/AI';
import { getRevenueById } from 'api/v2/revenues';
import { getExpenseById } from 'api/v3/expenses';
import Spinner from 'applications/web/pages/UserSignup/Shared/Spinner';
import cn from 'classnames';
import { Button } from 'components/atoms/Button';
import { mapExpenseMetadata } from 'contexts/AIMetadataProvider/useExpenseAiMetadata';
import { mapRevenueMetadata } from 'contexts/AIMetadataProvider/useRevenueAiMetadata';
import useWorldTheme, { worldThemeContext } from 'hooks/shared/useWorldTheme';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import { RenderGartification } from 'hooks/useGratifications';
import useModal from 'hooks/useModal';
import _isEqualWith from 'lodash/isEqualWith';
import _isNil from 'lodash/isNil';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import { useQuery } from 'react-query';
import type { ConversationMetadata } from 'types/ai.types';
import {
  AICacheKeys,
  ExpensesCacheKeys,
  RevenuesCacheKeys,
} from 'types/cacheKeys.types';
import { PartialIExpenseV2 } from 'types/expenses.types';
import type { Invoice } from 'types/invoice.types';
import { intlFormat } from 'utils/formatMoney';
import { useAIChatContext } from './AIChat.context';
import { useGetGratificationById } from './hooks';
import {
  cardIconColors,
  cardIconComponents,
  sharedMarkdownComponents,
} from './utils';

const isEqualExcludingNil = (v1: any, v2: any): boolean | undefined => {
  if (_isNil(v1) && _isNil(v2)) return true; // both values are null or undefined, consider them equal
  if (_isNil(v1) || _isNil(v2)) return false; // one of the values is null or undefined, consider them unequal
  return undefined; // Fallback to default comparison for other values
};

const useIsDocumentUpdated = (metadata?: ConversationMetadata) => {
  const { documentId: docId, documentType: docType } = metadata || ({} as any);

  const queryKey =
    docType === 'expense'
      ? ExpensesCacheKeys.ExpenseDetails
      : RevenuesCacheKeys.RevenueDetails;
  const queryFn = docType === 'expense' ? getExpenseById : getRevenueById;

  const { data: docData, isLoading } = useQuery<PartialIExpenseV2 | Invoice>(
    [queryKey, docId],
    () => queryFn(docId!),
    { enabled: !!docId && ['expense', 'revenue'].includes(docType) },
  );

  if (!metadata || !docData) return { isEdited: false, isLoading };

  const updatedData =
    docType === 'expense'
      ? mapExpenseMetadata(docData as PartialIExpenseV2)
      : mapRevenueMetadata(docData as Partial<Invoice>);

  const isEdited =
    'data' in metadata &&
    'data' in updatedData &&
    !_isEqualWith(updatedData.data, metadata.data, isEqualExcludingNil);

  return { isEdited, isLoading };
};

const useNavigationPath = (metadata?: ConversationMetadata) => {
  switch (metadata?.documentType) {
    case 'expense':
      return `/expenses?id=${metadata.documentId}`;
    case 'revenue':
      return `/revenues?id=${metadata.documentId}`;
    case 'transaction':
      return `/banks?id=${metadata.documentId}`;
  }
};

const getTranslationVariables = (
  metadata?: ConversationMetadata,
): Record<string, string> => {
  if (!metadata?.data) return {};

  const { date, currency = 'EUR', amount: originalAmount } = metadata.data;

  const amount = intlFormat({
    currency,
    amount: originalAmount,
  });

  switch (metadata.documentType) {
    case 'expense':
      return { date, amount, merchant: metadata.data.supplier?.name };
    case 'revenue':
      return { date, amount, merchant: metadata.data.client?.name };
    case 'transaction':
      return { date, amount, merchant: metadata.data.counterPartyName };
    default:
      return {};
  }
};

const useSourceMetadata = (conversationId?: string, source = 'default') => {
  const { data: { data: metadataItems } = {}, isLoading } = useQuery(
    [AICacheKeys.ConversationMetadata, conversationId],
    () => getConversationMetadata(conversationId!),
    { enabled: !!conversationId && source !== 'default' },
  );

  const metadata = metadataItems?.find((m) => m.documentType === source);
  return { metadata, isLoading };
};

const AIChatSourceButton: FC<{
  metadata: ConversationMetadata;
  onClick?: () => void;
  isLoading?: boolean;
  title: string;
  content: string;
  isEdited?: boolean;
}> = ({ metadata, onClick, isLoading, title, content, isEdited }) => {
  const { t } = useTranslation();

  const IconComponent = (cardIconComponents as any)[metadata.documentType];
  const { color, bg } = (cardIconColors as any)[metadata.documentType];

  return (
    <Button
      asDefault
      className={cn(
        'text-primary-400 flex py-2 px-4 border border-primary-100 w-full text-left rounded-lg justify-between items-center',
        { 'cursor-pointer': onClick },
      )}
      onClick={onClick}
      disabled={isLoading || !onClick}
    >
      <div className="flex items-center gap-2 w-full">
        <div
          className={cn(
            'p-2 rounded-full flex items-center justify-center self-start',
            bg,
          )}
        >
          <IconComponent className={cn('w-4 h-4', color)} />
        </div>
        <div className="w-full">
          <h3 className="font-semibold text-primary-800">{title}</h3>

          <ReactMarkdown
            components={sharedMarkdownComponents}
            className="text-sm"
          >
            {content}
          </ReactMarkdown>
        </div>
        {isEdited && (
          <div className="mr-3 bg-taxes-100 text-taxes-700 rounded-full px-2 py-1 font-medium">
            {t('ai_bot.source.edited')}
          </div>
        )}
      </div>

      {isLoading ? (
        <Spinner />
      ) : onClick ? (
        <div className="flex items-center justify-center bg-primary-100 rounded-lg p-2">
          <EyeIcon className="w-5 md:w-5 font-light text-primary inline-block" />
        </div>
      ) : null}
    </Button>
  );
};

const AIChatSourceDocument: FC<{
  metadata: ConversationMetadata;
}> = ({ metadata }) => {
  const { t } = useTranslation();

  const navigate = useCustomNavigate();
  const navigationPath = useNavigationPath(metadata);
  const onClick = () => navigationPath && navigate(navigationPath);

  const { isEdited, isLoading } = useIsDocumentUpdated(metadata);
  // TODO: retranslate ai_bot.source.expense.title
  return (
    <AIChatSourceButton
      metadata={metadata}
      onClick={onClick}
      isLoading={isLoading}
      title={t(`ai_bot.source.${metadata.documentType}.title2`)}
      content={t(
        `ai_bot.source.${metadata.documentType}.description`,
        getTranslationVariables(metadata),
      )}
      isEdited={isEdited}
    />
  );
};

const AIChatSourceGratification: FC<{
  metadata: ConversationMetadata;
}> = ({ metadata }) => {
  const [modalContent, modalApi] = useModal({ width: '2xl', name: '' });
  const color = useWorldTheme();

  const gratificationId = metadata.documentId!;
  const { data: gratification, isLoading } =
    useGetGratificationById(gratificationId);

  const onClick = () =>
    gratification?.gratifContent &&
    modalApi.open(
      () => (
        <worldThemeContext.Provider value={color}>
          <RenderGartification
            key={gratificationId}
            id={gratificationId}
            gratification={gratification.gratifContent}
            metaData={gratification!.metadata}
            closeGratification={modalApi.close}
          />
        </worldThemeContext.Provider>
      ),
      { name: 'gratification details' },
    );

  const { title, description } = gratification || {};
  if (!title || !description) return null;

  return (
    <div className="flex">
      <AIChatSourceButton
        metadata={metadata}
        onClick={onClick}
        isLoading={isLoading}
        title={title}
        content={description}
      />
      {modalContent}
    </div>
  );
};

const AIChatSourceHelper: FC<{
  metadata: ConversationMetadata;
}> = ({ metadata }) => {
  const { t } = useTranslation();
  if (!metadata?.content) return null;

  return (
    <AIChatSourceButton
      metadata={metadata}
      title={t('ai_bot.source.helper')}
      content={metadata.content.replaceAll('\n', '\n\n')}
    />
  );
};

const AIChatSource: FC = () => {
  const { conversation } = useAIChatContext();
  const { _id: conversationId, source } = conversation || {};

  const { metadata } = useSourceMetadata(conversationId, source);

  switch (metadata?.documentType) {
    case 'expense':
    case 'transaction':
    case 'revenue':
      return <AIChatSourceDocument metadata={metadata} />;
    case 'gratifications':
      return <AIChatSourceGratification metadata={metadata} />;
    case 'helper':
      return <AIChatSourceHelper metadata={metadata} />;
    default:
      return null;
  }
};

export default AIChatSource;
